import { useEffect, useRef, useState } from 'react';
import { academyApi } from '../../services/academyApi';
import type { AcademyChapter } from '@platform/shared-types';
import type { Enqueue } from './CurriculumTab';
import QuestionEditor from './QuestionEditor';

/** How long typing must pause before the chapter autosaves. */
const AUTOSAVE_MS = 800;

export default function ChapterEditor(
  { chapter, moduleId, onSave, enqueue, status }:
  {
    chapter: AcademyChapter;
    moduleId: string;
    onSave: (next: AcademyChapter) => Promise<void>;
    enqueue: Enqueue;
    status: React.ReactNode;
  },
) {
  const [draft, setDraft] = useState<AcademyChapter>(chapter);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');

  /** Latest draft + whether it differs from what has been sent. */
  const draftRef = useRef(draft);
  const dirtyRef = useRef(false);
  draftRef.current = draft;

  const flush = () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    void onSave(draftRef.current);
  };

  /**
   * Autosave. Fields are never disabled and there is no page reload, so an
   * edit is saved whether the user blurs, keeps typing, or switches away.
   */
  useEffect(() => {
    if (!dirtyRef.current) return;
    const t = setTimeout(flush, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [draft]);

  /**
   * Flush on unmount. Switching chapters remounts this component (parent keys
   * on chapterId), so without this the last edits before a switch would be
   * lost — which is exactly how the summary field used to lose text.
   */
  useEffect(() => flush, []);

  const edit = (patch: Partial<AcademyChapter>) => {
    dirtyRef.current = true;
    setDraft((d) => ({ ...d, ...patch }));
  };

  const onFile = async (file: File) => {
    // Guard on `progress !== null`: during the presign+upload phase
    // draft.video is still falsy, so without this a second file picked
    // mid-upload starts a concurrent upload sharing this same `progress`
    // state, and whichever save resolves last silently wins the wrong video.
    if (progress !== null) return;
    setError('');
    setProgress(0);
    try {
      const { upload, video } = await enqueue(() => academyApi.presignVideo({
        moduleId,
        chapterId: draftRef.current.chapterId,
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      }));
      await academyApi.uploadVideo(file, upload, setProgress);
      const next = { ...draftRef.current, video };
      setDraft(next);
      dirtyRef.current = false;
      await onSave(next);
    } catch (e: any) {
      setError(e.message || 'That upload did not finish. Try again.');
    } finally {
      setProgress(null);
    }
  };

  const field = 'mt-1 w-full min-h-[44px] bg-white border border-gray-300 rounded-lg px-3 text-sm text-gray-900 outline-none focus:border-gray-900 transition';
  const label = 'text-[11px] uppercase tracking-[0.15em] text-gray-500 font-semibold';

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-gray-900">Chapter</h3>
          {status}
        </div>

        <label className="block">
          <span className={label}>Title</span>
          <input
            value={draft.title}
            onChange={(e) => edit({ title: e.target.value })}
            onBlur={flush}
            className={field}
          />
        </label>

        <label className="block">
          <span className={label}>What the rep should take away</span>
          <textarea
            value={draft.summary}
            onChange={(e) => edit({ summary: e.target.value })}
            onBlur={flush}
            rows={3}
            className="mt-1 w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-900 transition"
          />
        </label>

        <div>
          <span className={label}>Video</span>
          {draft.video ? (
            <video
              src={draft.video.cdnUrl}
              controls
              className="mt-2 w-full rounded-xl border border-gray-200 aspect-video bg-black"
            />
          ) : (
            <label
              className={`mt-2 flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-xl p-8 transition ${
                progress !== null
                  ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                  : 'border-gray-300 cursor-pointer hover:border-gray-900 hover:bg-white'
              }`}
            >
              <span className="text-sm text-gray-600">
                Drop a video here or <span className="text-gray-900 font-semibold underline">browse</span>
              </span>
              <span className="text-xs text-gray-400">
                MP4 / WebM / MOV · up to 2 GB · 1080p or lower plays best
              </span>
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                disabled={progress !== null}
                className="hidden"
                onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
              />
            </label>
          )}

          {progress !== null && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Uploading direct to S3</span>
                <span className="tabular-nums">{progress}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-gray-900 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-2">
              {error}
            </p>
          )}
        </div>

        <label className="flex items-center justify-between min-h-[44px]">
          <span className="text-sm text-gray-900">Required before the exam unlocks</span>
          <input
            type="checkbox"
            checked={draft.required}
            onChange={(e) => {
              const next = { ...draftRef.current, required: e.target.checked };
              dirtyRef.current = false;
              setDraft(next);
              void onSave(next);
            }}
            className="w-5 h-5 accent-gray-900"
          />
        </label>
      </div>

      <QuestionEditor chapterId={draft.chapterId} enqueue={enqueue} />
    </div>
  );
}
